'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useTaxRate, useDeleteTaxRates } from '@/lib/api/taxRates'
import { useToast } from '@/components/ui/use-toast'

interface DeleteTaxRateModalProps {
	taxRateId?: string
	onClose: () => void
	onSuccess?: () => void
}

interface DeleteTaxRateModalContentProps {
	taxRateId: string
	onClose: () => void
	onSuccess?: () => void
}

export const DeleteTaxRateModal = ({ taxRateId, onClose, onSuccess }: DeleteTaxRateModalProps) => {
	if (!taxRateId) return null
	return <DeleteTaxRateModalContent taxRateId={taxRateId} onClose={onClose} onSuccess={onSuccess} />
}

const DeleteTaxRateModalContent = ({ taxRateId, onClose, onSuccess }: DeleteTaxRateModalContentProps) => {
	const { t } = useTranslation('taxes')
	const { t: tCommon } = useTranslation('common')
	const { toast } = useToast()
	const { data: taxRateData, isPending: isLoadingTaxRate } = useTaxRate(taxRateId)
	const deleteTaxRates = useDeleteTaxRates()

	const handleDelete = async () => {
		if (!taxRateId) return

		try {
			await deleteTaxRates.mutateAsync({ ids: [taxRateId] })

			toast({
				title: tCommon('success'),
				description: t('deleteSuccess'),
			})

			onSuccess?.()
			onClose()
		} catch (error) {
			toast({
				title: tCommon('error'),
				description: t('deleteError'),
				variant: 'destructive',
			})
		}
	}

	const taxRateName = taxRateData?.taxRate?.name || t('taxRate')

	return (
		<AlertDialog open={true} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('modals.delete.title')}</AlertDialogTitle>
					<AlertDialogDescription>
						{isLoadingTaxRate
							? t('modals.delete.loading')
							: t('modals.delete.description', { name: taxRateName })}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleteTaxRates.isPending}>
						{tCommon('cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={deleteTaxRates.isPending || isLoadingTaxRate}
					>
						{deleteTaxRates.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{tCommon('delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

